/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { fetchCustomersServices } from "../requests/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";

interface Service {
  id: number;
  name: string;
  subscriptionId: string;
  type: string;
}

export interface RawCustomer {
  id: number;
  name: string;
  nameDisplay?: string;
  services: {
    ports: Service[];
    internet: Service[];
    cloudConnectivity: Service[];
  };
}

export interface ServiceItem {
  id: number;
  name: string;
  subscriptionId: string;
  type: string;
  customer: { id: number; name: string };
}

/**
 * @param tenantId
 * @param isCustomerView
 * @param serviceType
 */

export function useCustomers(
  tenantId: number,
  isCustomerView: boolean,
  serviceType?: "port" | "internet" | "cloud-connectivity"
) {
  const [items, setItems] = useState<ServiceItem[]>([]);
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const api = useAxiosAuth0();

  useEffect(() => {
    if (!tenantId) {
      setItems([]);
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    fetchCustomersServices(api, tenantId)
      .then((resp) => {
        const customers: RawCustomer[] = resp.data.data;
        const flat: ServiceItem[] = [];

        customers.forEach((cust) => {
          const custName = cust.nameDisplay ?? cust.name;
          let svcArray: Service[] = [];
          switch (serviceType) {
            case "port":
              svcArray = cust.services.ports;
              break;
            case "internet":
              svcArray = cust.services.internet;
              break;
            case "cloud-connectivity":
              svcArray = cust.services.cloudConnectivity;
              break;
            default:
              svcArray = [
                ...cust.services.ports,
                ...cust.services.internet,
                ...cust.services.cloudConnectivity,
              ];
          }

          svcArray.forEach((svc) =>
            flat.push({
              id: svc.id,
              name: svc.name,
              subscriptionId: svc.subscriptionId,
              type: svc.type,
              customer: { id: cust.id, name: custName },
            })
          );
        });

        setItems(flat);
        setOptions(
          flat.map((it) => ({
            value: it.id.toString(),
            label: isCustomerView
              ? it.name
              : `${it.customer.name} – ${it.name}`,
          }))
        );
      })
      .catch((err) => {
        console.error("Failed to load customers with services", err);
      })
      .finally(() => setLoading(false));
  }, [api, tenantId, isCustomerView, serviceType]);

  return { items, options, loading };
}
